import { useEffect, useState } from "react";
import { Check, PencilSimple, Trophy, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fileUrl, formatApiErrorDetail } from "../../lib/api";
import { notifyContentUpdated } from "../../lib/contentSync";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import ImageEditorModal from "./ImageEditorModal";

const LEVEL_OPTIONS = ["Internal Klub", "Kota/Kabupaten", "Provinsi", "Nasional", "Internasional"];
const MEDAL_OPTIONS = [
    { value: "gold", label: "Emas" },
    { value: "silver", label: "Perak" },
    { value: "bronze", label: "Perunggu" },
];
const CATEGORY_OPTIONS = ["Speed", "Standart", "Slalom"];

export default function ParentAchievementsReview() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [editorSrc, setEditorSrc] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/parent-achievements?status=pending");
            setItems(data);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    useEffect(() => {
        load();
    }, []);

    const approve = async (item) => {
        try {
            await api.post(`/admin/parent-achievements/${item.id}/approve`);
            toast.success("Prestasi disetujui");
            notifyContentUpdated();
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    const reject = async (item) => {
        if (!window.confirm(`Tolak prestasi "${item.title}" dari ${item.athlete}?`)) return;
        try {
            await api.post(`/admin/parent-achievements/${item.id}/reject`);
            toast.success("Prestasi ditolak");
            notifyContentUpdated();
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    const openEdit = (item) => {
        setEditing(item);
        setForm({
            athlete: item.athlete,
            title: item.title,
            event: item.event,
            year: item.year,
            medal: item.medal,
            category: item.category,
            level: item.level || "Kota/Kabupaten",
            image_url: item.image_url || "",
        });
    };

    const pickPhoto = (file) => {
        if (!file) return;
        setEditorSrc(URL.createObjectURL(file));
    };

    const editExistingPhoto = async () => {
        try {
            const res = await fetch(fileUrl(form.image_url));
            const blob = await res.blob();
            setEditorSrc(URL.createObjectURL(blob));
        } catch {
            toast.error("Gagal memuat foto untuk diedit");
        }
    };

    const closeEditor = () => {
        if (editorSrc) URL.revokeObjectURL(editorSrc);
        setEditorSrc(null);
    };

    const onEditorSave = async (blob) => {
        closeEditor();
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", blob, "foto.jpg");
            const { data } = await api.post("/admin/upload", fd);
            setForm((prev) => ({ ...prev, image_url: data.url }));
            toast.success("Foto terunggah");
        } catch {
            toast.error("Gagal mengunggah foto");
        } finally {
            setUploading(false);
        }
    };

    const saveEdit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put(`/admin/achievements/${editing.id}`, { ...form, year: Number(form.year), order: editing.order ?? 0 });
            toast.success("Pengajuan diperbarui — silakan ACC jika sudah sesuai");
            notifyContentUpdated();
            setEditing(null);
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <section className="mb-8 border border-racing-line bg-white" data-testid="parent-achievement-review">
            <div className="p-5 border-b border-racing-line flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                    <h3 className="flex items-center gap-2 font-display uppercase text-xl"><Trophy size={20} className="text-racing-red" /> Prestasi dari Orang Tua</h3>
                    <p className="text-sm text-racing-muted mt-1">Periksa, edit jika perlu, lalu setujui sebelum prestasi tampil di halaman publik.</p>
                </div>
                <span className="text-xs uppercase tracking-[0.2em] text-racing-muted" data-testid="parent-achievement-count">{items.length} pending</span>
            </div>
            {items.length === 0 ? (
                <p className="p-6 text-sm text-racing-muted" data-testid="parent-achievement-review-empty">Tidak ada pengajuan prestasi yang menunggu ACC.</p>
            ) : (
                <div className="divide-y divide-racing-line">
                    {items.map((item, index) => (
                        <div key={item.id} className="p-5 flex flex-col lg:flex-row lg:items-center gap-4" data-testid={`parent-achievement-review-${index}`}>
                            {item.image_url && <img src={fileUrl(item.image_url)} alt="" className="w-24 h-20 object-cover border border-racing-line" />}
                            <div className="flex-1">
                                <p className="font-display uppercase text-xl">{item.title}</p>
                                <p className="text-sm text-racing-muted mt-1">{item.athlete} • {item.event} • {item.year} • {item.category}{item.level ? ` • ${item.level}` : ""}</p>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => openEdit(item)} data-testid={`achievement-edit-${index}`} className="flex items-center gap-2 border border-racing-line text-racing-muted hover:text-neutral-900 hover:border-black/40 px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors"><PencilSimple size={15} /> Edit</button>
                                <button onClick={() => approve(item)} data-testid={`achievement-approve-${index}`} className="flex items-center gap-2 bg-green-700 text-white px-4 py-2 text-xs uppercase tracking-[0.2em]"><Check size={15} /> ACC</button>
                                <button onClick={() => reject(item)} data-testid={`achievement-reject-${index}`} className="flex items-center gap-2 border border-racing-red text-racing-red px-4 py-2 text-xs uppercase tracking-[0.2em]"><X size={15} /> Tolak</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
                <DialogContent className="bg-white border border-racing-line text-neutral-900 max-w-lg max-h-[88vh] overflow-y-auto" data-testid="achievement-edit-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display uppercase tracking-wide text-xl">Edit Pengajuan Prestasi</DialogTitle>
                    </DialogHeader>
                    {form && (
                        <form onSubmit={saveEdit} className="space-y-4 mt-2" data-testid="achievement-edit-form">
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Nama Atlet</label>
                                <input required value={form.athlete} onChange={(e) => setForm({ ...form, athlete: e.target.value })} data-testid="edit-achievement-athlete" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Nomor Lomba</label>
                                <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="edit-achievement-title" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Nama Event</label>
                                <input required value={form.event} onChange={(e) => setForm({ ...form, event: e.target.value })} data-testid="edit-achievement-event" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Tahun</label>
                                    <input required type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="edit-achievement-year" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" />
                                </div>
                                <div>
                                    <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Medali</label>
                                    <select value={form.medal} onChange={(e) => setForm({ ...form, medal: e.target.value })} data-testid="edit-achievement-medal" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red">
                                        {MEDAL_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Kategori</label>
                                    <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="edit-achievement-category" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red">
                                        {CATEGORY_OPTIONS.map((c) => <option key={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Kejuaraan Tingkat</label>
                                    <select value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} data-testid="edit-achievement-level" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red">
                                        {LEVEL_OPTIONS.map((l) => <option key={l}>{l}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Foto Prestasi (Opsional)</label>
                                {form.image_url ? (
                                    <div className="mb-2 flex items-end gap-3">
                                        <img src={fileUrl(form.image_url)} alt="" className="h-20 w-28 object-cover border border-racing-line" />
                                        <button type="button" onClick={editExistingPhoto} data-testid="edit-achievement-photo-edit" className="border border-racing-line px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-racing-muted hover:text-racing-red hover:border-racing-red transition-colors">Edit Foto</button>
                                    </div>
                                ) : null}
                                <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; pickPhoto(f); }} data-testid="edit-achievement-photo-input" className="w-full text-xs text-racing-muted" />
                                {uploading && <p className="mt-1 text-xs text-racing-red">Mengunggah...</p>}
                            </div>
                            <button type="submit" disabled={saving || uploading} data-testid="edit-achievement-save" className="w-full bg-racing-red hover:bg-racing-redhover disabled:opacity-50 text-white text-xs uppercase tracking-[0.25em] py-3.5 transition-colors">
                                {saving ? "Menyimpan..." : "Simpan Perubahan"}
                            </button>
                        </form>
                    )}
                </DialogContent>
            </Dialog>

            <ImageEditorModal
                open={!!editorSrc}
                imageSrc={editorSrc}
                aspect={4 / 3}
                title="Edit Foto Prestasi"
                onCancel={closeEditor}
                onSave={onEditorSave}
            />
        </section>
    );
}
